// Каталог стволов — CRUD над `/api/public/wellbores`. OSDU Wellbore, нижний
// уровень онтологии Field→Well→Wellbore. По паттерну WellsPage/FieldsPage (list +
// Editor в одном файле, refetch вместо оптимистики, list↔editor через
// search-параметры ?edit=N / ?new=1).
//
// Поля: name (обяз.), well_id (родительская скважина — `<select>` по каталогу
// /api/public/wells, ОБЯЗАТЕЛЬНА: ствол без скважины бессмыслен), description
// (текст), trajectory_type (профиль ствола — `<select>` по справочнику OSDU
// `WellboreTrajectoryType`, пусто = «не указано»), kickoff_md / total_depth_md
// (измеренные глубины, м — опциональны, пусто = NULL).
//
// `is_default` («основной» ствол) — read-only флаг с сервера: у каждой скважины
// ровно один такой ствол, он создаётся автоматически и служит якорем её данных.
// В UI это бейдж «(основной)», заблокированные удаление и смена скважины
// (сервер на обе операции отвечает 409).

import { createSignal, createResource, createMemo, Show, For, onMount, onCleanup } from 'solid-js'
import type { Component } from 'solid-js'
import { Icon } from './Icons'
import {
  listWellbores,
  createWellbore,
  updateWellbore,
  deleteWellbore,
  setDefaultWellbore,
  InUseError,
} from './api/wellbores'
import type { WellboreDto } from './api/wellbores'
import { listWells } from './api/wells'
import type { WellDto } from './api/wells'

// Профиль ствола — действующие коды справочника OSDU `WellboreTrajectoryType`
// (решение Стаса 2026-09-28: полный справочник). value — код, который хранит
// сервер (он же судит его при записи); label — код и имя справочника, как на
// десктопе. Источник — манифест
// `rust_app/docs/osdu/sources/ReferenceValues/WellboreTrajectoryType.1.json`;
// снятый `Directional` сюда не входит. «Боковой ствол» прежнего списка — не
// профиль, а зарезка (родитель ствола), и снят. '' = «не указано».
const TRAJECTORY_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'Vertical',              label: 'Vertical' },
  { value: 'Inclined',              label: 'Inclined' },
  { value: 'Inclined.DeepInclined', label: 'Inclined.DeepInclined — Deep inclined' },
  { value: 'Inclined.SlantHole',    label: 'Inclined.SlantHole — Slant hole' },
  { value: 'Inclined.SType',        label: 'Inclined.SType — S-type' },
  { value: 'Horizontal',            label: 'Horizontal' },
  { value: 'Horizontal.Level',      label: 'Horizontal.Level — Level' },
  { value: 'Horizontal.ToeDown',    label: 'Horizontal.ToeDown — Toe-down' },
  { value: 'Horizontal.ToeUp',      label: 'Horizontal.ToeUp — Toe-up' },
  { value: 'Horizontal.Undulating', label: 'Horizontal.Undulating — Undulating' },
]
const trajectoryLabel = (v: string | null): string =>
  v == null || v === '' ? '—' : (TRAJECTORY_OPTIONS.find((o) => o.value === v)?.label ?? v)

// ────────────────────── редактор ──────────────────────

type EditorState = {
  /// `null` — создаём. Иначе редактируем.
  source:         WellboreDto | null
  name:           string
  /// Родительская скважина. `null` — ещё не выбрана (на submit обязательна).
  wellId:         number | null
  description:    string
  trajectoryType: string
  /// Глубины как строки — текстовое поле, парсим на submit. Пусто = не задано.
  kickoffMd:      string
  totalDepthMd:   string
}

function emptyEditor(): EditorState {
  return {
    source:         null,
    name:           '',
    wellId:         null,
    description:    '',
    trajectoryType: '',
    kickoffMd:      '',
    totalDepthMd:   '',
  }
}

// `null` глубина (не задана) → пустая строка в форме.
const depthStr = (v: number | null): string => (v == null ? '' : String(v))

function editorFrom(w: WellboreDto): EditorState {
  return {
    source:         w,
    name:           w.name,
    wellId:         w.well_id,
    description:    w.description,
    // `null` (не указан) → пустой пункт выбора.
    trajectoryType: w.trajectory_type ?? '',
    kickoffMd:      depthStr(w.kickoff_md),
    totalDepthMd:   depthStr(w.total_depth_md),
  }
}

type EditorProps = {
  state:   EditorState
  wells:   WellDto[]
  onClose: () => void
  onSaved: () => void
}

const Editor: Component<EditorProps> = (props) => {
  const initial = props.state
  const [st, setSt] = createSignal<EditorState>(props.state)
  const [busy, setBusy] = createSignal(false)
  const [err, setErr] = createSignal<string | null>(null)

  const isCreate = () => st().source === null
  const noWells = () => props.wells.length === 0
  // Дефолтный («основной») ствол скважины — якорь её данных: сервер запрещает
  // и удаление, и перенос под другую скважину (409). Селектор скважины для
  // него блокируем, чтобы юзер не упирался в 409 после заполнения формы.
  const isDefault = () => st().source?.is_default === true

  const isDirty = createMemo<boolean>(() => {
    const c = st()
    if (isCreate()) {
      return (
        c.name.trim().length > 0 ||
        c.wellId != null ||
        c.description.trim().length > 0 ||
        c.trajectoryType.length > 0 ||
        c.kickoffMd.trim().length > 0 ||
        c.totalDepthMd.trim().length > 0
      )
    }
    return (
      c.name !== initial.name
      || c.wellId !== initial.wellId
      || c.description !== initial.description
      || c.trajectoryType !== initial.trajectoryType
      || c.kickoffMd !== initial.kickoffMd
      || c.totalDepthMd !== initial.totalDepthMd
    )
  })

  const tryClose = () => {
    if (busy()) return
    if (isDirty() && !confirm('Несохранённые изменения будут потеряны. Закрыть?')) return
    props.onClose()
  }

  onMount(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        tryClose()
      }
    }
    window.addEventListener('keydown', onKey)
    onCleanup(() => window.removeEventListener('keydown', onKey))
  })

  // Опциональная глубина: пусто → null; иначе неотрицательное число.
  // Возвращает `number | null` при успехе, либо строку-сообщение об ошибке.
  const parseDepth = (raw: string, label: string): number | null | string => {
    const trimmed = raw.trim()
    if (trimmed === '') return null
    const n = Number(trimmed)
    if (!Number.isFinite(n)) return `Поле ${label}: ожидается число`
    if (n < 0) return `Поле ${label}: глубина не может быть отрицательной`
    return n
  }

  const onSubmit = async (e: Event) => {
    e.preventDefault()
    setErr(null)
    const cur = st()
    const nameTrimmed = cur.name.trim()
    if (nameTrimmed.length === 0) {
      setErr('Название обязательно')
      return
    }
    if (cur.wellId == null) {
      setErr('Скважина обязательна')
      return
    }

    const kickoff = parseDepth(cur.kickoffMd, 'забуривание (MD)')
    if (typeof kickoff === 'string') { setErr(kickoff); return }
    const td = parseDepth(cur.totalDepthMd, 'общая глубина (MD)')
    if (typeof td === 'string') { setErr(td); return }

    setBusy(true)
    try {
      if (isCreate()) {
        await createWellbore({
          well_id:         cur.wellId,
          name:            nameTrimmed,
          description:     cur.description,
          trajectory_type: cur.trajectoryType,
          kickoff_md:      kickoff,
          total_depth_md:  td,
          // Слот куста формой не задаётся: `null` = «берётся со скважины» (create) /
          // «не трогать» (patch) — то самое поведение, что было до появления полей.
          wellhead_latitude:  null,
          wellhead_longitude: null,
        })
      } else {
        await updateWellbore(cur.source!.id, {
          well_id:         cur.wellId,
          name:            nameTrimmed,
          description:     cur.description,
          trajectory_type: cur.trajectoryType,
          // Пустое поле глубины → СТЕРЕТЬ её (clear_*=true; сервер обнулит
          // колонку, проигнорировав присланное число). Тот же паттерн, что у
          // пикеров скважины и площадки. До 2026-09-01 флагов не было, и
          // очищенное поле молча не сохранялось: сервер читал `null` как «не
          // трогать» и оставлял старую глубину.
          kickoff_md:      kickoff,
          clear_kickoff_md:     kickoff === null,
          total_depth_md:  td,
          clear_total_depth_md: td === null,
          // Слот куста формой не задаётся вовсе, поэтому не «стереть», а «не
          // трогать»: очистка того, чего форма не показывает, снимала бы
          // человеку слот, которого он не видел.
          wellhead_latitude:  null,
          wellhead_longitude: null,
          clear_wellhead:     false,
        })
      }
      props.onSaved()
    } catch (e) {
      setErr(String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form class="equipment-editor" onSubmit={onSubmit}>
      <div class="equipment-editor-head">
        <button class="btn btn-ghost" type="button" onClick={() => tryClose()} disabled={busy()}>
          ← К каталогу
        </button>
        <div class="equipment-editor-title">
          <div class="uppercase">{isCreate() ? 'Новый ствол' : 'Редактирование'}</div>
          <h1>{isCreate() ? 'Создать ствол' : st().name || 'Ствол'}</h1>
        </div>
        <div class="equipment-editor-actions">
          <button class="btn" type="button" onClick={() => tryClose()} disabled={busy()}>
            Отмена
          </button>
          <button class="btn btn-primary" type="submit" disabled={busy() || !isDirty()}>
            {busy() ? 'Сохраняем…' : isCreate() ? 'Создать' : 'Сохранить'}
          </button>
        </div>
      </div>

      <Show when={err()}>
        <div class="card equipment-error">{err()}</div>
      </Show>

      <Show when={noWells()}>
        <div class="card equipment-error">
          Нет ни одной скважины — сначала создайте скважину в каталоге «Все скважины».
        </div>
      </Show>

      <main class="equipment-form-panel">
        <section class="equipment-form-section">
          <div class="equipment-form-main">
            <label class="field">
              <div class="field-label">Название</div>
              <input
                class="field-input"
                value={st().name}
                onInput={(e) => setSt({ ...st(), name: e.currentTarget.value })}
                placeholder="Напр., Основной ствол"
                autofocus
              />
            </label>

            <label class="field">
              <div class="field-label">
                Скважина
                <Show when={isDefault()}>
                  {' '}
                  <span class="muted text-xs">— основной ствол, перенос запрещён</span>
                </Show>
              </div>
              <select
                class="field-input"
                disabled={isDefault()}
                value={st().wellId == null ? '' : String(st().wellId)}
                onChange={(e) => {
                  const v = e.currentTarget.value
                  setSt({ ...st(), wellId: v === '' ? null : Number(v) })
                }}
              >
                <option value="">— выберите скважину —</option>
                <For each={props.wells}>
                  {(w) => <option value={String(w.id)}>{w.name}</option>}
                </For>
              </select>
            </label>

            <label class="field">
              <div class="field-label">Тип траектории</div>
              <select
                class="field-input"
                value={st().trajectoryType}
                onChange={(e) => setSt({ ...st(), trajectoryType: e.currentTarget.value })}
              >
                <option value="">— не указано —</option>
                <For each={TRAJECTORY_OPTIONS}>
                  {(o) => <option value={o.value}>{o.label}</option>}
                </For>
              </select>
            </label>

            <label class="field">
              <div class="field-label">Описание</div>
              <textarea
                class="field-input"
                rows={3}
                value={st().description}
                onInput={(e) => setSt({ ...st(), description: e.currentTarget.value })}
                placeholder="Произвольное примечание"
              />
            </label>
          </div>

          <div class="field">
            <div class="field-label">Глубины ствола (MD)</div>
            <div class="dim-grid equipment-dim-grid">
              <div class="dim-cell">
                <div class="dim-label">
                  <span class="dim-symbol">Забуривание, м</span>
                  <span class="dim-hint">MD точки kick-off</span>
                </div>
                <input
                  class="field-input mono"
                  value={st().kickoffMd}
                  onInput={(e) => setSt({ ...st(), kickoffMd: e.currentTarget.value })}
                  inputmode="decimal"
                  placeholder="1500"
                />
              </div>

              <div class="dim-cell">
                <div class="dim-label">
                  <span class="dim-symbol">Общая, м</span>
                  <span class="dim-hint">MD забоя ствола</span>
                </div>
                <input
                  class="field-input mono"
                  value={st().totalDepthMd}
                  onInput={(e) => setSt({ ...st(), totalDepthMd: e.currentTarget.value })}
                  inputmode="decimal"
                  placeholder="3200"
                />
              </div>
            </div>
          </div>
        </section>
      </main>
    </form>
  )
}

// ────────────────────── страница ──────────────────────

type WellboresPageProps = {
  wellboreId?: number | null
  create?:     boolean
  onOpen:      (id: number) => void
  onCreate:    () => void
  onBack:      () => void
}

export const WellboresPage: Component<WellboresPageProps> = (props) => {
  const [items, { refetch }] = createResource(() => listWellbores())
  const pageItems = createMemo<WellboreDto[]>(() => items()?.items ?? [])
  // Каталог скважин — для селектора родителя и для колонки «Скважина».
  const [wells] = createResource(() => listWells({ limit: 200 }))
  const [delErr, setDelErr] = createSignal<string | null>(null)
  const [filterText, setFilterText] = createSignal('')

  const wellList = createMemo<WellDto[]>(() => wells()?.items ?? [])
  // id → имя скважины, для отрисовки родителя в таблице.
  const wellNameById = createMemo<Record<number, string>>(() => {
    const map: Record<number, string> = {}
    for (const w of wellList()) map[w.id] = w.name
    return map
  })

  const editor = createMemo<EditorState | null>(() => {
    if (props.create) return emptyEditor()
    if (props.wellboreId == null) return null
    const item = pageItems().find((w) => w.id === props.wellboreId)
    return item ? editorFrom(item) : null
  })

  const filtered = createMemo<WellboreDto[]>(() => {
    const all = pageItems()
    const q = filterText().trim().toLowerCase()
    if (q.length === 0) return all
    const names = wellNameById()
    return all.filter((w) =>
      w.name.toLowerCase().includes(q)
      || (w.description ?? '').toLowerCase().includes(q)
      || (names[w.well_id] ?? '').toLowerCase().includes(q)
    )
  })

  const openCreate = () => props.onCreate()
  const openEdit = (w: WellboreDto) => props.onOpen(w.id)
  const closeEditor = () => props.onBack()
  const onSaved = () => {
    refetch()
    props.onBack()
  }

  // Пересадка роли основного ствола. Подтверждение обязательно и написано
  // прямым текстом: последствие невидимо в этой таблице — всё, что адресовано
  // СКВАЖИНОЙ без явного ствола (загрузки, привязки, старые проекты, знающие
  // только скважину), после этого поедет в новый ствол. Данные прежнего
  // основного остаются на нём.
  const onPromote = async (w: WellboreDto) => {
    const msg = `Сделать «${w.name}» основным стволом скважины?\n\n`
      + 'Данные прежнего основного ствола останутся на нём. Изменится то, куда '
      + 'попадут новые данные, адресованные скважиной без указания ствола.'
    if (!confirm(msg)) return
    setDelErr(null)
    try {
      await setDefaultWellbore(w.id)
      refetch()
    } catch (err) {
      setDelErr(String(err))
    }
  }

  const onDelete = async (w: WellboreDto) => {
    if (!confirm(`Удалить «${w.name}»?`)) return
    setDelErr(null)
    try {
      await deleteWellbore(w.id)
      refetch()
    } catch (err) {
      if (err instanceof InUseError) setDelErr(err.message)
      else setDelErr(String(err))
    }
  }

  const wellSummary = (w: WellboreDto): string =>
    wellNameById()[w.well_id] ?? `#${w.well_id}`

  const depthSummary = (w: WellboreDto): string =>
    w.kickoff_md == null && w.total_depth_md == null
      ? '—'
      : `${w.kickoff_md ?? '?'} / ${w.total_depth_md ?? '?'}`

  return (
    <Show
      when={editor()}
      keyed
      fallback={<div class="page page-wide">
      <div class="page-header">
        <div>
          <h1 class="page-title">Каталог стволов</h1>
          <p class="page-subtitle">
            Стволы (OSDU Wellbore) — нижний уровень онтологии
            Field→Well→Wellbore. Родительская скважина, тип траектории и глубины.
          </p>
        </div>
        <div class="row">
          <button class="btn btn-primary" onClick={openCreate}>
            <Icon name="plus" />Ствол
          </button>
        </div>
      </div>

      <Show when={delErr()}>
        <div class="card" style={{ padding: 'var(--sp-3) var(--sp-4)', 'margin-bottom': 'var(--sp-4)', color: 'var(--status-err)' }}>
          {delErr()}
        </div>
      </Show>

      <Show when={items.loading}>
        <div class="card" style={{ padding: 'var(--sp-5)' }}>Загрузка…</div>
      </Show>

      <Show when={items.error}>
        <div class="card" style={{ padding: 'var(--sp-5)', color: 'var(--status-err)' }}>
          Ошибка: {String(items.error)}
        </div>
      </Show>

      <Show when={items() && items()!.total === 0}>
        <div class="card" style={{ padding: 'var(--sp-7)', 'text-align': 'center' }}>
          <div style={{ 'font-size': 'var(--fs-md)', 'margin-bottom': 'var(--sp-2)' }}>
            Каталог пуст
          </div>
          <div class="muted text-sm" style={{ 'margin-bottom': 'var(--sp-4)' }}>
            Создайте первый ствол
          </div>
          <button class="btn btn-primary" onClick={openCreate}>
            <Icon name="plus" />Добавить ствол
          </button>
        </div>
      </Show>

      <Show when={items() && items()!.total > 0}>
        <div class="row filter-row" style={{ gap: 'var(--sp-3)', 'margin-bottom': 'var(--sp-4)' }}>
          <div class="input" style={{ flex: 1, 'max-width': '320px' }}>
            <Icon name="search" />
            <input
              placeholder="Поиск по названию / скважине"
              value={filterText()}
              onInput={(e) => setFilterText(e.currentTarget.value)}
            />
            <Show when={filterText().length > 0}>
              <button
                class="btn-icon btn-ghost"
                style={{ width: '24px', height: '24px' }}
                title="Очистить"
                onClick={() => setFilterText('')}
              >
                <Icon name="x" size={12} />
              </button>
            </Show>
          </div>
          <div class="muted text-sm filter-counter" style={{ 'margin-left': 'auto', 'align-self': 'center' }}>
            Показано {filtered().length} из {items()!.total}
          </div>
        </div>

        <div class="card">
          <table class="tbl">
            <thead>
              <tr>
                <th style={{ width: '110px' }}>ID</th>
                <th>Название</th>
                <th>Скважина</th>
                <th>Траектория</th>
                <th>Глубины, м (kick-off / забой)</th>
                <th>Описание</th>
                <th style={{ width: '80px' }} />
              </tr>
            </thead>
            <tbody>
              <For each={filtered()}>
                {(w) => (
                  <tr style={{ cursor: 'pointer' }} onClick={() => openEdit(w)}>
                    <td class="mono text-xs muted">#{w.id}</td>
                    <td style={{ 'font-weight': 500 }}>
                      {w.name}
                      <Show when={w.is_default}>
                        {' '}
                        <span class="muted text-xs" title="Дефолтный ствол скважины — на нём висят её данные, удалить нельзя">
                          (основной)
                        </span>
                      </Show>
                    </td>
                    <td class="text-xs">{wellSummary(w)}</td>
                    <td class="text-xs">{trajectoryLabel(w.trajectory_type)}</td>
                    <td class="mono text-xs">{depthSummary(w)}</td>
                    <td class="text-xs muted">{w.description || '—'}</td>
                    <td>
                      {/* Сделать основным — только для НЕосновного: на основном
                          кнопка была бы no-op'ом. Роль пересаживается, данные
                          прежнего основного остаются на нём. */}
                      <Show when={!w.is_default}>
                        <button
                          class="btn-icon"
                          title="Сделать основным стволом скважины"
                          onClick={(ev) => {
                            ev.stopPropagation()
                            onPromote(w)
                          }}
                        >
                          <Icon name="check" />
                        </button>
                      </Show>
                      <button
                        class="btn-icon"
                        title={w.is_default ? 'Основной ствол удалить нельзя' : 'Удалить'}
                        disabled={w.is_default}
                        onClick={(ev) => {
                          ev.stopPropagation()
                          onDelete(w)
                        }}
                      >
                        <Icon name="x" />
                      </button>
                    </td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
          <Show when={filtered().length === 0}>
            <div style={{ padding: 'var(--sp-5)', 'text-align': 'center' }} class="muted text-sm">
              Ничего не найдено по фильтру
            </div>
          </Show>
        </div>
      </Show>

      </div>}
    >
      {(ed) => (
        <div class="page page-wide">
          <Editor state={ed} wells={wellList()} onClose={closeEditor} onSaved={onSaved} />
        </div>
      )}
    </Show>
  )
}
